import { Geolocation } from '@capacitor/geolocation';
import { getNestedProperty } from '@deckee/utilities';
import { telemetry } from '../backend';
import { hasFeatureEnabled } from '../feature';
import { getStatic, remove, set } from '../local-storage';
import { definitions, emit } from '../message-broker';
import { isLocationAvailable, isLocationEnabled } from '../permissions';
import { getStore } from '../store';
import { moveMapToLocation } from '../store/modules/app-controls';
import { actions } from '../store/modules/geo-service';

let previousLatitude;
let previousLongitude;

const updateCoords =
  ({ overriding = false }) =>
  async (data) => {
    // If we are in the process of overwriting the users location, ignore any updates.
    if (
      !overriding &&
      getStore().getState().geoService.currentlyOverridingPosition
    )
      return;

    if (!data || data.coords === undefined) {
      return;
    }

    const {
      coords: { latitude, longitude },
    } = data;

    // Check the last values, if they are the same ignore this update request.
    if (latitude === previousLatitude && longitude === previousLongitude)
      return;
    previousLatitude = latitude;
    previousLongitude = longitude;

    // Watch here, data is a Position object that doesn't stringify
    await set('geoService', 'lastKnownPosition', {
      coords: {
        latitude: +latitude,
        longitude: +longitude,
      },
    });

    const { followModeActive, tripPaused } = getStore().getState().logTrips;

    if (followModeActive && !tripPaused) {
      await getStore().dispatch(
        moveMapToLocation({
          center: {
            lat: +latitude,
            lon: +longitude,
          },
        }),
      );
    }

    emit(definitions.map.userLocationUpdated, {
      lat: +latitude,
      lon: +longitude,
    });

    await getStore().dispatch(
      actions.updateCurrentPosition(
        { lat: +latitude, lon: +longitude },
        overriding,
      ),
    );
  };

/**
 * @param  {function} successCallback - Callback to run if getting current position from navigator geolocation works
 * @param  {function} errorCallback - Callback to run if getting current position from navigator geolocation doesn't work
 * @param  {object} options - Options for navigator geolocation get current position function. Defaults are maximumAge: 0, timeout: 10000.
 */
export const geoLocationGetCurrentPosition = ({
  successCallback = () => {
    /* Customise this */
  },
  errorCallback = (err) => {
    throw new Error(err);
  },
  // We don't want to get cached results from the device here, so maximumAge is left to the default
  // Timeout is retained, although we do handle this on the front end to deal with last received position
  opts = {
    maximumAge: 0,
    timeout: 10000,
  },
}) => {
  const runGetCurrentLocation = async () => {
    const options = {
      ...opts,
      enableHighAccuracy: !hasFeatureEnabled('disable-high-accuracy'), // Always set `enableHighAccuracy` based off the feature, not the params passed in
    };

    try {
      const result = await Geolocation.getCurrentPosition(options);
      successCallback(result);
    } catch (e) {
      if (!e?.message?.includes('User denied')) {
        telemetry(
          telemetry.ERROR,
          `[geo.geoLocationGetCurrentPosition] Error retrieving ${JSON.stringify(
            e,
          )}`,
        );
      }
      errorCallback(e);
    }
  };

  runGetCurrentLocation();
};

export const updateCurrentPosition = () => {
  geoLocationGetCurrentPosition({
    successCallback: updateCoords({ overriding: false }),
    errorCallback: (/* err */) => {
      // Can't get it, oh well...
    },
  });
};

export const startWatchingPosition = () => {
  const runWatchPosition = async () => {
    try {
      await Geolocation.watchPosition(
        {
          enableHighAccuracy: !hasFeatureEnabled('disable-high-accuracy'),
          timeout: 5000,
          maximumAge: 0,
        },
        updateCoords({ overriding: false }),
      );
    } catch (e) {
      telemetry(
        telemetry.ERROR,
        `[geo.startWatingPosition] Error retrieving ${JSON.stringify(e)}`,
      );
    }
  };
  runWatchPosition();
};

export const setOverridingPosition = (givenCoords) => {
  if (givenCoords) {
    const positionObject = {
      coords: {
        latitude: +givenCoords.latitude,
        longitude: +givenCoords.longitude,
      },
    };
    updateCoords({ overriding: true })(positionObject);
    set('geoService', 'overridingPosition', positionObject);
  } else {
    remove('geoService', 'overridingPosition');
    updateCurrentPosition();
    getStore().dispatch(actions.clearOverridingPosition());
  }
};

/**
 * This differs from getCurrentPositionIfEnabled as it checks that the device setting is enabled AND the application is authorized to use location.
 * getCurrentPositionIfEnabled only checks that the device setting is enabled.
 *
 * @param  {function} allStateCallback - Callback that runs on error (with a default position of nulls), and on success (with a position from the plugin)
 */
export const getCurrentPositionIfAvailable = async () => {
  const defaultPosition = {
    latitude: null,
    longitude: null,
    speed: null,
    heading: null,
    altitude: null,
    altitudeAccuracy: null,
    accuracy: null,
  };

  try {
    const locationAvailable = await isLocationAvailable();

    if (locationAvailable) {
      return await new Promise((resolve, reject) => {
        geoLocationGetCurrentPosition({
          successCallback: resolve,
          errorCallback: reject,
        });
      }).then((data) => {
        if (data && data.coords) {
          return data.coords;
        }

        return defaultPosition;
      });
    }

    telemetry(
      telemetry.DEBUG,
      `[getCurrentPositionIfAvailable] Can't get location - using default position`,
    );

    return defaultPosition;
  } catch (error) {
    telemetry(
      telemetry.DEBUG,
      `[getCurrentPositionIfAvailable] Can't get location - using default position. Reasons why is within: ${JSON.stringify(
        error,
      )}`,
    );

    return defaultPosition;
  }
};

export const getCurrentPositionIfEnabled = async () => {
  const currentPosition = {
    latitude: null,
    longitude: null,
  };

  const locationEnabled = await isLocationEnabled();

  if (locationEnabled) {
    await new Promise((resolve, _) => {
      geoLocationGetCurrentPosition({
        successCallback: (data) => {
          if (!data || data.coords === undefined) {
            resolve();
            return;
          }
          const {
            coords: { latitude, longitude },
          } = data;
          currentPosition.latitude = latitude;
          currentPosition.longitude = longitude;
          resolve();
        },
        errorCallback: (err) => {
          telemetry(
            telemetry.DEBUG,
            `[data-capture/getCurrentPosition] Can't get location. Reasons why is within: ${JSON.stringify(
              err,
            )}`,
          );
          // Oh well...
          resolve();
        },
      });
    });
  }

  return currentPosition;
};

export const getCurrentPositionOrMapCenter = async () => {
  try {
    const currentPosition = await getCurrentPositionIfAvailable();

    if (currentPosition.latitude && currentPosition.longitude) {
      return currentPosition;
    }
    // eslint-disable-next-line no-empty
  } catch (e) {}
  const { lat: latitude, lon: longitude } =
    getStore().getState().appControls.currentMapParams.viewPortDetails.center;

  return {
    latitude,
    longitude,
  };
};

export const initialize = () => {
  const overridingPosition = getStatic('geoService', 'overridingPosition');
  const lastKnownPosition = getStatic('geoService', 'lastKnownPosition');
  if (
    getNestedProperty(overridingPosition, 'coords.latitude') &&
    getNestedProperty(overridingPosition, 'coords.longitude')
  ) {
    updateCoords({ overriding: true })(overridingPosition);
  } else if (
    getNestedProperty(lastKnownPosition, 'coords.latitude') &&
    getNestedProperty(lastKnownPosition, 'coords.longitude')
  ) {
    updateCoords({ overriding: false })(lastKnownPosition);
  }

  updateCurrentPosition();
  startWatchingPosition();
};
